import { api, useSessionStore } from '@/shared/api';
import { unwrap, unwrapCollection, type Page } from '@/shared/api/envelope';

import {
    toAuditLogEntry,
    toImpersonationSession,
    toImpersonationSessionRecord,
    toManualBookingResult,
    toPlans,
    toPlatformMetrics,
    toPlatformTemplate,
    toPlatformTenant,
    toProvisionedTenant,
    toTemplateRollouts,
    type AuditLogEntry,
    type AuditLogFilters,
    type CreditRules,
    type ImpersonationSession,
    type ImpersonationSessionRecord,
    type ManualBookingResult,
    type Plan,
    type PlatformMetricFilters,
    type NewTenantInput,
    type PlatformMetrics,
    type PlatformTemplate,
    type PlatformTenant,
    type ProvisionedTenant,
    type TemplateRollout,
} from './platform-model';
import {
    auditLogResourceSchema,
    creditAdjustmentResourceSchema,
    impersonationSessionResourceSchema,
    impersonationTicketResourceSchema,
    planResourceSchema,
    platformMetricResourceSchema,
    platformTemplateResourceSchema,
    platformTenantResourceSchema,
    provisionedTenantResourceSchema,
    templateRolloutResourceSchema,
} from './types';

export interface PlatformTenantFilters {
    search?: string;
    status?: string;
    page?: number;
}

/** Schrijfacties gaan nooit in de offline cache. */
const WRITE = { cacheKey: false } as const;

/**
 * Het eigen token van de beheerder. Tijdens meekijken draagt de http-client het
 * tijdelijke token van de ander, en daarmee bestaat `/platform` niet (404).
 */
function asPlatformUser() {
    const token = useSessionStore.getState().session?.token;

    return token ? { token } : {};
}

/**
 * Alles onder `/platform`, alleen voor `platform_admin`
 * (`02-api-contract.md` §Platformbeheer). De tenant komt uit het pad en is
 * altijd de **uuid**; er gaat geen `X-Tenant` mee.
 */
export const platformApi = {
    async tenants(filters: PlatformTenantFilters = {}): Promise<Page<PlatformTenant>> {
        const page = unwrapCollection(
            platformTenantResourceSchema,
            await api.get('/platform/tenants', {
                query: {
                    search: filters.search || undefined,
                    status: filters.status || undefined,
                    page: filters.page,
                    per_page: 50,
                },
            }),
        );

        return { items: page.items.map(toPlatformTenant), meta: page.meta };
    },

    /**
     * Een keuringsbedrijf aanmaken, in één transactie: het bedrijf in proefperiode,
     * de eerste eigenaar (uitgenodigd, of meteen lid als het adres al een account
     * heeft), de wallet met startsaldo en de gekozen vragenlijsten.
     */
    async createTenant(input: NewTenantInput): Promise<ProvisionedTenant> {
        return toProvisionedTenant(
            unwrap(
                provisionedTenantResourceSchema,
                await api.post(
                    '/platform/tenants',
                    {
                        name: input.name,
                        slug: input.slug,
                        email: input.email,
                        phone: input.phone,
                        starting_credits: input.startingCredits,
                        owner: {
                            name: input.owner.name,
                            email: input.owner.email,
                            phone: input.owner.phone,
                        },
                        templates: input.templateUuids,
                    },
                    { ...WRITE, timeoutMs: 60_000 },
                ),
            ),
        );
    },

    /** De vragenlijsten die het platform levert. */
    async templates(): Promise<PlatformTemplate[]> {
        return unwrapCollection(
            platformTemplateResourceSchema,
            await api.get('/platform/templates'),
        ).items.map(toPlatformTemplate);
    },

    /**
     * Eén vragenlijst naar hoogstens 50 bedrijven. Idempotent: een bedrijf dat
     * hem al heeft, krijgt `skipped`.
     */
    async rollOutTemplate(input: {
        templateUuid: string;
        tenantUuids: readonly string[];
    }): Promise<TemplateRollout[]> {
        return toTemplateRollouts(
            unwrapCollection(
                templateRolloutResourceSchema,
                await api.post(
                    '/platform/templates/rollout',
                    { template_uuid: input.templateUuid, tenant_uuids: [...input.tenantUuids] },
                    { ...WRITE, timeoutMs: 120_000 },
                ),
            ).items,
            input.tenantUuids,
        );
    },

    async tenant(tenantUuid: string): Promise<PlatformTenant> {
        return toPlatformTenant(
            unwrap(platformTenantResourceSchema, await api.get(`/platform/tenants/${tenantUuid}`)),
        );
    },

    /** Opschorten: niemand van dit bedrijf kan nog werken. Reden verplicht. */
    async suspend(tenantUuid: string, reason: string): Promise<PlatformTenant> {
        return toPlatformTenant(
            unwrap(
                platformTenantResourceSchema,
                await api.post(`/platform/tenants/${tenantUuid}/suspend`, { reason }, WRITE),
            ),
        );
    },

    async resume(tenantUuid: string): Promise<PlatformTenant> {
        return toPlatformTenant(
            unwrap(
                platformTenantResourceSchema,
                await api.post(`/platform/tenants/${tenantUuid}/resume`, undefined, WRITE),
            ),
        );
    },

    /** Tegoedregels: blokkeren bij nul en de ruimte onder nul. Altijd allebei. */
    async changeCreditRules(tenantUuid: string, rules: CreditRules): Promise<PlatformTenant> {
        return toPlatformTenant(
            unwrap(
                platformTenantResourceSchema,
                await api.patch(
                    `/platform/tenants/${tenantUuid}/credit-rules`,
                    { block_on_empty: rules.blockOnEmpty, grace_credits: rules.graceCredits },
                    WRITE,
                ),
            ),
        );
    },

    async changePlan(tenantUuid: string, planCode: string): Promise<PlatformTenant> {
        return toPlatformTenant(
            unwrap(
                platformTenantResourceSchema,
                await api.patch(
                    `/platform/tenants/${tenantUuid}/plan`,
                    { plan_code: planCode },
                    WRITE,
                ),
            ),
        );
    },

    /**
     * De abonnementsvormen. `/plans` leest binnen een omgeving; de beheerder
     * geeft die omgeving expliciet mee, want hij heeft er zelf geen.
     */
    async plans(tenantUuid: string): Promise<Plan[]> {
        return toPlans(
            unwrapCollection(
                planResourceSchema,
                await api.get('/plans', { headers: { 'X-Tenant': tenantUuid } }),
            ).items,
        );
    },

    /**
     * Handmatige tegoedboeking in hele credits. **Reden verplicht** en gelogd met
     * de beheerder erbij. Idempotent op de sleutel: een dubbele klik boekt één keer.
     */
    async bookCredits(input: {
        tenantUuid: string;
        credits: number;
        reason: string;
        idempotencyKey: string;
    }): Promise<ManualBookingResult> {
        return toManualBookingResult(
            unwrap(
                creditAdjustmentResourceSchema,
                await api.post(
                    `/platform/tenants/${input.tenantUuid}/credits`,
                    { amount: input.credits, reason: input.reason },
                    { ...WRITE, idempotencyKey: input.idempotencyKey },
                ),
            ),
        );
    },

    /**
     * Meekijken als een **persoon bij een bepaald bedrijf**: je impersoneert een
     * lidmaatschap. Reden verplicht, hoogstens een uur.
     */
    async impersonate(input: {
        tenantUuid: string;
        tenantName: string;
        userUuid: string;
        userName: string;
        reason: string;
        minutes?: number;
    }): Promise<ImpersonationSession> {
        const ticket = unwrap(
            impersonationTicketResourceSchema,
            await api.post(
                `/platform/tenants/${input.tenantUuid}/impersonate`,
                { user_uuid: input.userUuid, reason: input.reason, minutes: input.minutes },
                WRITE,
            ),
        );

        return toImpersonationSession(ticket, input);
    },

    async impersonations(tenantUuid: string): Promise<ImpersonationSessionRecord[]> {
        return unwrapCollection(
            impersonationSessionResourceSchema,
            await api.get(`/platform/tenants/${tenantUuid}/impersonations`, { cacheKey: false }),
        ).items.map(toImpersonationSessionRecord);
    },

    /** Sessie beëindigen en het tijdelijke token intrekken - met het eigen token. */
    async endImpersonation(sessionUuid: string): Promise<void> {
        await api.delete(`/platform/impersonations/${sessionUuid}`, {
            ...WRITE,
            ...asPlatformUser(),
            expect: 'none',
        });
    },

    async metrics(filters: PlatformMetricFilters = {}): Promise<PlatformMetrics> {
        return toPlatformMetrics(
            unwrapCollection(
                platformMetricResourceSchema,
                await api.get('/platform/metrics', {
                    query: {
                        tenant: filters.tenantUuid,
                        from: filters.from,
                        until: filters.until,
                        per_page: 100,
                    },
                }),
            ).items,
        );
    },

    async auditLogs(filters: AuditLogFilters = {}): Promise<Page<AuditLogEntry>> {
        const page = unwrapCollection(
            auditLogResourceSchema,
            await api.get('/platform/audit-logs', {
                query: {
                    tenant: filters.tenantUuid || undefined,
                    log_name: filters.logName || undefined,
                    from: filters.from || undefined,
                    until: filters.until || undefined,
                    impersonated_only: filters.impersonatedOnly ? 1 : undefined,
                    page: filters.page,
                },
            }),
        );

        return { items: page.items.map(toAuditLogEntry), meta: page.meta };
    },
};
